/**
 * Borrowing Calculator Page Component (Task 106)
 *
 * Indicative pledging capacity calculator based on collateral positions.
 * Applies LTV ratios, collateral eligibility rules, and concentration limits.
 * Note: the lending institution extends credit and sets terms. Whether
 * securities-secured credit is purpose credit, and the margin limits that
 * follow, are determined under 12 CFR 221 (Regulation U). Aaim lends nothing.
 */
import { useState, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { advisorClient } from '../api/client';
import { AdvisorServiceMissingError, AdvisorServiceNotice, advisorFailure } from '../components/AdvisorServiceNotice';
import type { components } from '@aaimplatform/institutional-types/advisor-tools';
import {
  Card,
  Heading,
  BodyText,
  FormLabel,
  Input,
  Button,
  MetricDisplay,
  FormHelperText,
  Checkbox,
} from '@aaimplatform/ui';


type Position = components['schemas']['Position'];
interface CalculationResult {
  totalMarketValue: number;
  totalCollateralValue: number;
  maxBorrowingCapacity: number;
  recommendedBorrowing: number;
  safetyBuffer: number;
  collateralRequirement: number;
  concentrationWarnings: string[];
  ltvCompliant: boolean;
}

export function BorrowingCalculatorPage() {
  const [searchParams] = useSearchParams();
  const clientId = searchParams.get('clientId');

  const [positions, setPositions] = useState<(Position & { included: boolean; ltvRatio?: number })[]>([]);
  const [requestedAmount, setRequestedAmount] = useState<number>(0);
  const [ltvTarget, setLtvTarget] = useState<number>(0.70); // 70% default

  // Fetch client positions if clientId provided
  const { data: clientData, isLoading, error } = useQuery({
    queryKey: ['client-positions', clientId],
    queryFn: async () => {
      if (!clientId) return null;
      const { data, error, response } = await advisorClient.GET('/api/clients/{clientId}/positions', {
        params: { path: { clientId } },
      });
      if (error || !response.ok) throw advisorFailure(response, error, 'Failed to load client positions');
      return data;
    },
    enabled: !!clientId,
  });

  useEffect(() => {
    if (clientData?.positions) {
      setPositions(
        clientData.positions.map((p) => ({ ...p, included: true }))
      );
    }
  }, [clientData]);

  // Calculate borrowing capacity in real-time
  const calculation = useMemo<CalculationResult>(() => {
    const includedPositions = positions.filter((p) => p.included && p.isEligible);

    const totalMarketValue = includedPositions.reduce(
      (sum, p) => sum + p.marketValue,
      0
    );
    const totalCollateralValue = includedPositions.reduce(
      (sum, p) => sum + p.collateralValue,
      0
    );

    // Max borrowing capacity based on collateral value and LTV
    const maxBorrowingCapacity = totalCollateralValue * ltvTarget;

    // 80% of the maximum indicative capacity; the remainder is the safety buffer
    const recommendedBorrowing = maxBorrowingCapacity * 0.80;
    const safetyBuffer = maxBorrowingCapacity - recommendedBorrowing;

    // Collateral requirement: minimum collateral needed for requested amount
    const collateralRequirement = requestedAmount / ltvTarget;

    // Check concentration limits
    const concentrationWarnings: string[] = [];
    const positionsBySymbol = new Map<string, number>();

    includedPositions.forEach((p) => {
      const existing = positionsBySymbol.get(p.symbol) || 0;
      positionsBySymbol.set(p.symbol, existing + p.collateralValue);
    });

    positionsBySymbol.forEach((value, symbol) => {
      const concentration = value / totalCollateralValue;
      if (concentration > 0.25) {
        concentrationWarnings.push(
          `${symbol}: ${(concentration * 100).toFixed(1)}% concentration (max 25%)`
        );
      }
    });

    // LTV compliance check
    // For non-purpose loans, LTV is determined by collateral type and risk policy
    const ltvCompliant = ltvTarget <= 0.80; // Maximum 80% for institutional lending

    return {
      totalMarketValue,
      totalCollateralValue,
      maxBorrowingCapacity,
      recommendedBorrowing,
      safetyBuffer,
      collateralRequirement,
      concentrationWarnings,
      ltvCompliant,
    };
  }, [positions, requestedAmount, ltvTarget]);

  const togglePosition = (positionId: string) => {
    setPositions(
      positions.map((p) =>
        p.id === positionId ? { ...p, included: !p.included } : p
      )
    );
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const formatPercent = (decimal: number) => {
    return `${(decimal * 100).toFixed(1)}%`;
  };

  if (isLoading) {
    return (
      <div>
        <Card>
          <BodyText className="text-secondary text-center py-8">Loading positions...</BodyText>
        </Card>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <BodyText className="text-secondary mt-2">
            Compute the indicative pledging capacity of selected collateral positions for the lender to evaluate
          </BodyText>
        </div>
      </div>

      {error instanceof AdvisorServiceMissingError && <AdvisorServiceNotice className="mb-6" />}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        {/* Left: Position Selection */}
        <Card>
          <Heading level={3} as={3} size="card" className="mb-4">Select Collateral Positions</Heading>
          {positions.length > 0 ? (
            <div className="space-y-2">
              {positions.map((position) => (
                <div
                  key={position.id}
                  className={`p-4 border rounded-lg ${
                    !position.isEligible
                      ? 'border-surface bg-recessed'
                      : position.included
                        ? 'border-accent bg-accent/5'
                        : 'border-recessed bg-surface'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <Checkbox
                      label=""
                      checked={position.included}
                      onChange={() => togglePosition(position.id)}
                      disabled={!position.isEligible}
                      className="mt-1"
                    />
                    <div className="flex-1">
                      <div className="font-semibold text-primary">{position.symbol}</div>
                      <div className="text-sm text-secondary mb-2">
                        {position.description}
                      </div>
                      <div className="flex flex-wrap gap-4 text-sm">
                        <span className="text-primary">Market: {formatCurrency(position.marketValue)}</span>
                        <span className="text-primary">
                          Collateral:{' '}
                          {position.isEligible
                            ? formatCurrency(position.collateralValue)
                            : 'Not Eligible'}
                        </span>
                        <span className="text-primary">LTV: {formatPercent(position.ltvRatio ?? 0)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <BodyText className="text-secondary">
              No positions available. Select a client or add positions manually.
            </BodyText>
          )}

          <div className="mt-6 space-y-4">
            <div>
              <FormLabel htmlFor="ltvTarget">Target LTV Ratio</FormLabel>
              <Input
                id="ltvTarget"
                type="number"
                min={0}
                max={1}
                step={0.05}
                value={ltvTarget}
                onChange={(e) => setLtvTarget(parseFloat(e.target.value))}
              />
              <FormHelperText>
                Typical range: 50-70% depending on collateral type
              </FormHelperText>
            </div>

            <div>
              <FormLabel htmlFor="requestedAmount">Requested Loan Amount</FormLabel>
              <Input
                id="requestedAmount"
                type="number"
                min={0}
                step={1000}
                value={requestedAmount}
                onChange={(e) => setRequestedAmount(parseFloat(e.target.value))}
                placeholder="Enter amount"
              />
            </div>
          </div>
        </Card>

        {/* Right: Calculation Results */}
        <Card>
          <Heading level={3} as={3} size="card" className="mb-4">Indicative Pledging Capacity Analysis</Heading>

          <div className="space-y-4">
            <div className="flex justify-between items-center py-3 border-b border-surface">
              <BodyText className="text-secondary">Total Market Value</BodyText>
              <MetricDisplay
                value={formatCurrency(calculation.totalMarketValue)}
                className="text-primary"
              />
            </div>
            <div className="flex justify-between items-center py-3 border-b border-surface">
              <BodyText className="text-secondary">Total Collateral Value</BodyText>
              <MetricDisplay
                value={formatCurrency(calculation.totalCollateralValue)}
                className="text-primary"
              />
            </div>
            <div className="flex justify-between items-center py-3 border-b border-recessed bg-accent/5 px-4 -mx-4 rounded">
              <BodyText className="font-semibold text-accent">Maximum Indicative Pledging Capacity</BodyText>
              <MetricDisplay
                value={formatCurrency(calculation.maxBorrowingCapacity)}
                className="text-2xl text-accent"
              />
            </div>
            <div className="flex justify-between items-center py-3 border-b border-surface bg-growth/10 px-4 -mx-4 rounded">
              <BodyText className="text-primary">80% of Maximum Indicative Capacity</BodyText>
              <MetricDisplay
                value={formatCurrency(calculation.recommendedBorrowing)}
                className="text-growth"
              />
            </div>
            <div className="flex justify-between items-center py-3">
              <BodyText className="text-secondary">Safety Buffer</BodyText>
              <MetricDisplay
                value={formatCurrency(calculation.safetyBuffer)}
                className="text-primary"
              />
            </div>
          </div>

          {requestedAmount > 0 && (
            <div className="mt-6 pt-6 border-t border-recessed">
              <Heading level={3} as={3} size="card" className="mb-4">Requested Amount Analysis</Heading>
              <div className="space-y-3">
                <div className="flex justify-between items-center py-2">
                  <BodyText className="text-secondary">Requested Loan Amount</BodyText>
                  <MetricDisplay
                    value={formatCurrency(requestedAmount)}
                    className="text-primary"
                  />
                </div>
                <div className="flex justify-between items-center py-2">
                  <BodyText className="text-secondary">Required Collateral Value</BodyText>
                  <MetricDisplay
                    value={formatCurrency(calculation.collateralRequirement)}
                    className="text-primary"
                  />
                </div>
                <div className="flex justify-between items-center py-2">
                  <BodyText className="text-secondary">Current LTV</BodyText>
                  <MetricDisplay
                    value={
                      calculation.totalCollateralValue > 0
                        ? formatPercent(requestedAmount / calculation.totalCollateralValue)
                        : 'N/A'
                    }
                    className="text-primary"
                  />
                </div>
                {requestedAmount > calculation.maxBorrowingCapacity && (
                  <div className="p-4 bg-error/10 border border-error/20 rounded text-sm">
                    <strong className="text-error">Warning:</strong>{' '}
                    <span className="text-error">
                      Requested amount exceeds the maximum indicative pledging capacity. Additional collateral required:{' '}
                      {formatCurrency(
                        calculation.collateralRequirement - calculation.totalCollateralValue
                      )}
                    </span>
                  </div>
                )}
                {requestedAmount > calculation.recommendedBorrowing &&
                  requestedAmount <= calculation.maxBorrowingCapacity && (
                    <div className="p-4 bg-accent/5 border border-accent/20 rounded text-sm">
                      <strong className="text-accent">Note:</strong>{' '}
                      <span className="text-accent">
                        Requested amount is within maximum but exceeds recommended amount. Consider maintaining a safety buffer.
                      </span>
                    </div>
                  )}
              </div>
            </div>
          )}

          {/* Compliance & Warnings */}
          <div className="mt-6 pt-6 border-t border-recessed">
            <Heading level={3} as={3} size="card" className="mb-4">Compliance & Risk Checks</Heading>
            <div
              className={`inline-block px-4 py-2 rounded ${
                calculation.ltvCompliant
                  ? 'bg-growth/10 border border-growth/20 text-growth'
                  : 'bg-error/10 border border-error/20 text-error'
              }`}
            >
              {calculation.ltvCompliant
                ? 'LTV Within Guidelines'
                : 'LTV Exceeds Maximum'}
            </div>

            {calculation.concentrationWarnings.length > 0 && (
              <div className="mt-4 p-4 bg-warning/10 border border-warning/20 rounded">
                <strong className="text-warning">Concentration Warnings:</strong>
                <ul className="mt-2 ml-4 list-disc text-warning space-y-1">
                  {calculation.concentrationWarnings.map((warning, idx) => (
                    <li key={idx}>{warning}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </Card>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-wrap gap-3">
        <Button
          variant="primary"
          onClick={() =>
            window.open(
              `/proposals/new?clientId=${clientId}&amount=${requestedAmount}`,
              '_blank'
            )
          }
          disabled={!requestedAmount || requestedAmount > calculation.maxBorrowingCapacity}
        >
          Create Proposal with These Terms
        </Button>
        <Button variant="secondary" onClick={() => window.print()}>
          Print Report
        </Button>
      </div>
    </div>
  );
}
